import { useCallback, useEffect, useState } from 'react';
import { Link, useMatch, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Input, Select, Spin, message } from 'antd';
import classNames from 'classnames';
import { ArrowLeft, Printer } from 'lucide-react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
  createRecycleOrderReception,
  fetchRecycleOrderDetail,
  getRecycleOrderReception,
  getRecycleOrderReceptionById,
  getRecycleOrderReceptionPrefill,
  updateRecycleOrderReception,
} from 'apis/oms';
import { getMemberList } from 'apis/ums';
import XiaoMaLogoSvg from 'components/xiao-ma-logo-svg';
import { SHOP_MAP } from 'constant/shop';
import i18n from '../../../../i18n';
import LOCALS from 'commons/locals';
import { UmsMember } from 'types/ums';
import {
  ReceptionProfession,
  ReceptionSnsPlatform,
  ReceptionVisitMotive,
  RecycleOrderReception,
  RecycleOrderReceptionPayload,
  RecycleOrderReceptionPrefill,
} from 'types/oms';
import {
  RECEPTION_COPY,
  RECEPTION_LANGS,
  RECEPTION_LANG_LABELS,
  ReceptionCopy,
  ReceptionLang,
  resolveReceptionLang,
} from './locales';
import {
  RECEPTION_PROFESSION_OPTIONS,
  RECEPTION_SNS_PLATFORM_OPTIONS,
  RECEPTION_VISIT_MOTIVE_OPTIONS,
  isJapanReceptionStore,
} from './options';

dayjs.extend(utc);
dayjs.extend(timezone);

type FormState = {
  furigana: string;
  name: string;
  postCode: string;
  address: string;
  tel: string;
  email: string;
  profession?: ReceptionProfession;
  professionOther: string;
  visitMotive?: ReceptionVisitMotive;
  visitMotiveOther: string;
  snsPlatform?: ReceptionSnsPlatform;
};

const emptyForm = (): FormState => ({
  furigana: '',
  name: '',
  postCode: '',
  address: '',
  tel: '',
  email: '',
  professionOther: '',
  visitMotiveOther: '',
});

const formFromPrefill = (prefill?: RecycleOrderReceptionPrefill | null) => {
  const form = emptyForm();
  if (!prefill) return form;
  return {
    ...form,
    furigana: prefill.furigana || '',
    name: prefill.name || '',
    postCode: prefill.postCode || '',
    address: prefill.address || '',
    tel: prefill.tel || '',
    email: prefill.email || '',
  };
};

const formFromReception = (reception: RecycleOrderReception): FormState => ({
  furigana: reception.furigana || '',
  name: reception.name || '',
  postCode: reception.postCode || '',
  address: reception.address || '',
  tel: reception.tel || '',
  email: reception.email || '',
  profession: reception.profession,
  professionOther: reception.professionOther || '',
  visitMotive: reception.visitMotive,
  visitMotiveOther: reception.visitMotiveOther || '',
  snsPlatform: reception.snsPlatform || undefined,
});

type UnavailableKey = 'noSubmission' | 'notJapanStore' | 'loadFailed';

const validateForm = (form: FormState, copy: ReceptionCopy) => {
  if (!form.name.trim()) return copy.nameRequired;
  if (!form.postCode.trim()) return copy.postCodeRequired;
  if (!form.address.trim()) return copy.addressRequired;
  if (!form.tel.trim()) return copy.telRequired;
  if (!form.profession) return copy.professionRequired;
  if (form.profession === 'other' && !form.professionOther.trim()) {
    return copy.professionOtherRequired;
  }
  if (!form.visitMotive) return copy.visitMotiveRequired;
  if (form.visitMotive === 'other' && !form.visitMotiveOther.trim()) {
    return copy.visitMotiveOtherRequired;
  }
  if (form.visitMotive === 'sns' && !form.snsPlatform) {
    return copy.snsRequired;
  }
  return '';
};

const JAPAN_TIMEZONE = 'Asia/Tokyo';

const toPayload = (
  form: FormState,
  extra: { id?: number; submissionId?: number },
): RecycleOrderReceptionPayload => ({
  ...extra,
  furigana: form.furigana.trim(),
  name: form.name.trim(),
  postCode: form.postCode.trim(),
  address: form.address.trim(),
  tel: form.tel.trim(),
  email: form.email.trim(),
  profession: form.profession as ReceptionProfession,
  professionOther:
    form.profession === 'other' ? form.professionOther.trim() : '',
  visitMotive: form.visitMotive as ReceptionVisitMotive,
  visitMotiveOther:
    form.visitMotive === 'other' ? form.visitMotiveOther.trim() : '',
  snsPlatform: form.visitMotive === 'sns' ? form.snsPlatform : null,
});

const getReceptionDateParts = (time: string) => {
  const date = dayjs(time).tz(JAPAN_TIMEZONE);
  return {
    year: date.format('YYYY'),
    month: date.format('M'),
    day: date.format('D'),
  };
};

function ChoiceGrid<T extends string>({
  options,
  value,
  disabled,
  onChange,
}: {
  options: { value: T; label: string }[];
  value?: T;
  disabled?: boolean;
  onChange: (value: T) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            disabled={disabled}
            onClick={() => onChange(option.value)}
            className={classNames(
              'min-h-[72px] rounded-xl border-2 px-4 text-lg font-medium',
              selected
                ? 'border-neutral-900 bg-neutral-900 text-white'
                : 'border-neutral-200 bg-white text-neutral-900',
              disabled && 'cursor-default disabled:opacity-100',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

const FieldLabel = ({ children }: { children: string }) => {
  return (
    <div className="mb-2 text-base font-medium text-neutral-700">
      {children}
    </div>
  );
};

const ReadValue = ({ value }: { value?: string }) => {
  return (
    <div className="min-h-[56px] rounded-xl bg-neutral-50 px-4 py-3 text-lg">
      {value || '-'}
    </div>
  );
};

const RecycleOrderReceptionForm = () => {
  const navigate = useNavigate();
  const orderMatch = useMatch(
    '/rrs/recycling-consignment-detail/:id/reception',
  );
  const isNew = Boolean(useMatch('/rrs/reception/new'));
  const recordMatch = useMatch('/rrs/reception/:receptionId');
  const orderId = orderMatch?.params.id || '';
  const receptionId = isNew ? undefined : recordMatch?.params.receptionId;
  const [searchParams, setSearchParams] = useSearchParams();
  const lang = resolveReceptionLang(searchParams.get('lang'));
  const copy = RECEPTION_COPY[lang];
  const memberIdParam = searchParams.get('memberId');
  const direct = searchParams.get('direct') === '1';
  const showChooser = isNew && !memberIdParam && !direct;
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [unavailableReason, setUnavailableReason] =
    useState<UnavailableKey | null>(null);
  const [storeId, setStoreId] = useState<number>();
  const [submissionId, setSubmissionId] = useState<number>();
  const [reception, setReception] = useState<RecycleOrderReception | null>(
    null,
  );
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [memberOptions, setMemberOptions] = useState<UmsMember[]>([]);
  const [memberSearching, setMemberSearching] = useState(false);

  const load = useCallback(async () => {
    if (showChooser) {
      setLoading(false);
      setUnavailableReason(null);
      return;
    }

    setLoading(true);
    try {
      if (orderId) {
        const detailRes = await fetchRecycleOrderDetail({ id: orderId });
        const order = detailRes.data?.omsRecycleOrder;
        const nextStoreId = order?.storeId;
        const nextSubmissionId = order?.submissionId;
        setStoreId(nextStoreId);
        setSubmissionId(nextSubmissionId);

        if (!nextSubmissionId) {
          setUnavailableReason('noSubmission');
          return;
        }
        if (!isJapanReceptionStore(nextStoreId)) {
          setUnavailableReason('notJapanStore');
          return;
        }

        const receptionRes = await getRecycleOrderReception(nextSubmissionId);
        const nextReception = receptionRes.reception || null;
        setReception(nextReception);
        if (nextReception) {
          setForm(formFromReception(nextReception));
          setEditing(false);
        } else {
          setForm(formFromPrefill(receptionRes.prefill));
          setEditing(true);
        }
      } else if (isNew) {
        setStoreId(undefined);
        setSubmissionId(undefined);
        setReception(null);
        if (memberIdParam) {
          const prefill = await getRecycleOrderReceptionPrefill({
            memberId: Number(memberIdParam),
          });
          setForm(formFromPrefill(prefill));
        } else {
          setForm(emptyForm());
        }
        setEditing(true);
      } else if (receptionId) {
        const nextReception = await getRecycleOrderReceptionById(
          Number(receptionId),
        );
        setStoreId(undefined);
        setSubmissionId(nextReception.submissionId ?? undefined);
        setReception(nextReception);
        setForm(formFromReception(nextReception));
        setEditing(false);
      }
      setUnavailableReason(null);
    } catch {
      setUnavailableReason('loadFailed');
    } finally {
      setLoading(false);
    }
  }, [isNew, memberIdParam, orderId, receptionId, showChooser]);

  useEffect(() => {
    load();
  }, [load]);

  const patchForm = (patch: Partial<FormState>) => {
    setForm((current) => ({ ...current, ...patch }));
  };

  const handleSubmit = async () => {
    const error = validateForm(form, copy);
    if (error) {
      message.warning(error);
      return;
    }

    const payload = toPayload(
      form,
      reception ? { id: reception.id } : submissionId ? { submissionId } : {},
    );
    setSubmitting(true);
    try {
      if (reception) {
        const nextReception = await updateRecycleOrderReception(payload);
        setReception(nextReception);
        setForm(formFromReception(nextReception));
      } else if (submissionId) {
        await createRecycleOrderReception(payload);
        const receptionRes = await getRecycleOrderReception(submissionId);
        const nextReception = receptionRes.reception || null;
        setReception(nextReception);
        if (nextReception) setForm(formFromReception(nextReception));
      } else {
        const created = await createRecycleOrderReception(payload);
        message.success(copy.submitted);
        navigate(`/rrs/reception/${created.id}?lang=${lang}`, {
          replace: true,
        });
        return;
      }
      setEditing(false);
      message.success(copy.submitted);
    } finally {
      setSubmitting(false);
    }
  };

  const openDirectCreate = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('memberId');
    params.set('direct', '1');
    setSearchParams(params);
  };

  const searchMember = (keyword: string) => {
    const text = keyword.trim();
    if (!text) {
      setMemberOptions([]);
      return;
    }
    setMemberSearching(true);
    getMemberList({ keyword: text, pageNum: 1, pageSize: 10 })
      .then((res) => {
        setMemberOptions(res.data.list);
      })
      .finally(() => setMemberSearching(false));
  };

  const chooseMember = (memberId: number) => {
    const params = new URLSearchParams(searchParams);
    params.delete('direct');
    params.set('memberId', String(memberId));
    setSearchParams(params);
  };

  const setLang = (next: ReceptionLang) => {
    const params = new URLSearchParams(searchParams);
    params.set('lang', next);
    setSearchParams(params, { replace: true });
  };

  const professionOptions = RECEPTION_PROFESSION_OPTIONS.map((option) => ({
    value: option.value,
    label: copy.professionOptions[option.value],
  }));
  const visitMotiveOptions = RECEPTION_VISIT_MOTIVE_OPTIONS.map((option) => ({
    value: option.value,
    label: copy.visitMotiveOptions[option.value],
  }));
  const snsOptions = RECEPTION_SNS_PLATFORM_OPTIONS.map((option) => ({
    value: option.value,
    label: copy.snsOptions[option.value],
  }));

  const receptionDate = getReceptionDateParts(
    reception?.createTime || dayjs().toISOString(),
  );
  const backTo = orderId
    ? `/rrs/recycling-consignment-detail/${orderId}`
    : '/rrs/reception-list';

  const openPrint = () => {
    const params = new URLSearchParams();
    params.set('lang', lang);
    if (storeId != null) params.set('storeId', String(storeId));
    if (submissionId != null) params.set('submissionId', String(submissionId));
    params.set('year', receptionDate.year);
    params.set('month', receptionDate.month);
    params.set('day', receptionDate.day);
    const fields: Record<string, string> = {
      furigana: form.furigana,
      name: form.name,
      postCode: form.postCode,
      address: form.address,
      tel: form.tel,
      email: form.email,
      profession: form.profession || '',
      professionOther: form.profession === 'other' ? form.professionOther : '',
      visitMotive: form.visitMotive || '',
      visitMotiveOther:
        form.visitMotive === 'other' ? form.visitMotiveOther : '',
      snsPlatform: form.visitMotive === 'sns' ? form.snsPlatform || '' : '',
    };
    Object.entries(fields).forEach(([key, value]) => {
      const text = value.trim();
      if (text) params.set(key, text);
    });
    window.open(`/prints/reception?${params.toString()}`);
  };

  return (
    <div className="mx-auto max-w-3xl px-4 pb-28 pt-4">
      <div className="mb-2 flex items-center justify-between gap-3">
        <Link
          to={backTo}
          className="inline-flex items-center gap-1 text-sm text-black"
        >
          <ArrowLeft className="h-4 w-4" />
          {orderId ? copy.back : copy.backToList}
        </Link>
        <div className="flex items-center gap-1 text-sm">
          {RECEPTION_LANGS.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setLang(item)}
              className={classNames(
                'rounded px-2 py-1',
                item === lang
                  ? 'bg-neutral-900 text-white'
                  : 'text-neutral-500',
              )}
            >
              {RECEPTION_LANG_LABELS[item]}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="flex justify-center py-24">
          <Spin />
        </div>
      ) : showChooser ? (
        <div className="space-y-6 pt-8">
          <div className="text-center text-2xl font-medium">
            {i18n.t(LOCALS.new_reception_form)}
          </div>
          <Button block className="h-14 text-lg" onClick={openDirectCreate}>
            {i18n.t(LOCALS.reception_direct_create)}
          </Button>
          <div>
            <div className="mb-2 text-base text-neutral-600">
              {i18n.t(LOCALS.reception_select_member_hint)}
            </div>
            <Select
              showSearch
              filterOption={false}
              className="w-full"
              size="large"
              placeholder={i18n.t(LOCALS.phone_or_email)}
              loading={memberSearching}
              onSearch={searchMember}
              onChange={chooseMember}
              options={memberOptions.map((member) => ({
                value: member.id,
                label: `${member.showName || ''} (${member.email})`,
              }))}
            />
          </div>
        </div>
      ) : unavailableReason ? (
        <div className="rounded-xl bg-neutral-50 px-4 py-8 text-center text-base">
          {copy[unavailableReason]}
        </div>
      ) : (
        <div className="space-y-8">
          <header className="text-center text-neutral-900">
            <XiaoMaLogoSvg className="mx-auto h-20 w-auto" />
            {storeId === SHOP_MAP.UMEDA && (
              <div className="mt-1 text-sm tracking-[0.45em]">UMEDA</div>
            )}
            <div className="mt-8 text-2xl font-medium tracking-wide">
              {copy.formTitle}
            </div>
            <div className="mt-1 text-lg">{copy.formSubtitle}</div>
            <div className="mt-8 flex items-end justify-end gap-8 border-b border-neutral-900 pb-2">
              <div className="text-left leading-tight">
                <div>{copy.dateLabel}</div>
                {copy.dateSubLabel && (
                  <div className="text-xs">{copy.dateSubLabel}</div>
                )}
              </div>
              <div className="flex items-end gap-2 text-sm">
                <span className="min-w-16 text-center">
                  {receptionDate.year}
                </span>
                <span>{copy.year}</span>
                <span className="min-w-8 text-center">
                  {receptionDate.month}
                </span>
                <span>{copy.month}</span>
                <span className="min-w-8 text-center">{receptionDate.day}</span>
                {copy.day && <span>{copy.day}</span>}
              </div>
            </div>
          </header>

          <section className="space-y-5">
            <div>
              <FieldLabel>{copy.furigana}</FieldLabel>
              {editing ? (
                <Input
                  size="large"
                  className="h-14 text-lg"
                  value={form.furigana}
                  onChange={(event) =>
                    patchForm({ furigana: event.target.value })
                  }
                />
              ) : (
                <ReadValue value={form.furigana} />
              )}
            </div>
            <div>
              <FieldLabel>{copy.name}</FieldLabel>
              {editing ? (
                <Input
                  size="large"
                  className="h-14 text-lg"
                  value={form.name}
                  onChange={(event) => patchForm({ name: event.target.value })}
                />
              ) : (
                <ReadValue value={form.name} />
              )}
            </div>
            <div>
              <FieldLabel>{copy.postCode}</FieldLabel>
              {editing ? (
                <Input
                  size="large"
                  className="h-14 text-lg"
                  addonBefore="〒"
                  value={form.postCode}
                  onChange={(event) =>
                    patchForm({ postCode: event.target.value })
                  }
                />
              ) : (
                <ReadValue value={form.postCode ? `〒${form.postCode}` : ''} />
              )}
            </div>
            <div>
              <FieldLabel>{copy.address}</FieldLabel>
              {editing ? (
                <Input.TextArea
                  className="text-lg"
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  value={form.address}
                  onChange={(event) =>
                    patchForm({ address: event.target.value })
                  }
                />
              ) : (
                <ReadValue value={form.address} />
              )}
            </div>
            <div>
              <FieldLabel>{copy.tel}</FieldLabel>
              {editing ? (
                <Input
                  size="large"
                  className="h-14 text-lg"
                  inputMode="tel"
                  value={form.tel}
                  onChange={(event) => patchForm({ tel: event.target.value })}
                />
              ) : (
                <ReadValue value={form.tel} />
              )}
            </div>
            <div>
              <FieldLabel>{copy.email}</FieldLabel>
              {editing ? (
                <Input
                  size="large"
                  className="h-14 text-lg"
                  inputMode="email"
                  value={form.email}
                  onChange={(event) => patchForm({ email: event.target.value })}
                />
              ) : (
                <ReadValue value={form.email} />
              )}
            </div>
          </section>

          <section>
            <FieldLabel>{copy.profession}</FieldLabel>
            <div className="mb-3 text-sm text-neutral-500">
              {copy.selectOne}
            </div>
            <ChoiceGrid
              options={professionOptions}
              value={form.profession}
              disabled={!editing}
              onChange={(profession) => patchForm({ profession })}
            />
            {form.profession === 'other' && (
              <div className="mt-3">
                {editing ? (
                  <Input
                    size="large"
                    className="h-14 text-lg"
                    placeholder={copy.otherPlaceholder}
                    value={form.professionOther}
                    onChange={(event) =>
                      patchForm({ professionOther: event.target.value })
                    }
                  />
                ) : (
                  <ReadValue value={form.professionOther} />
                )}
              </div>
            )}
          </section>

          <div className="rounded-xl bg-neutral-50 px-4 py-3 text-sm leading-6 text-neutral-600">
            {copy.privacy}
          </div>

          <section>
            <FieldLabel>{copy.visitMotive}</FieldLabel>
            <div className="mb-3 text-sm text-neutral-500">
              {copy.selectOne}
            </div>
            <ChoiceGrid
              options={visitMotiveOptions}
              value={form.visitMotive}
              disabled={!editing}
              onChange={(visitMotive) =>
                patchForm({
                  visitMotive,
                  snsPlatform:
                    visitMotive === 'sns' ? form.snsPlatform : undefined,
                })
              }
            />
            {form.visitMotive === 'other' && (
              <div className="mt-3">
                {editing ? (
                  <Input
                    size="large"
                    className="h-14 text-lg"
                    placeholder={copy.otherPlaceholder}
                    value={form.visitMotiveOther}
                    onChange={(event) =>
                      patchForm({ visitMotiveOther: event.target.value })
                    }
                  />
                ) : (
                  <ReadValue value={form.visitMotiveOther} />
                )}
              </div>
            )}
          </section>

          {form.visitMotive === 'sns' && (
            <section>
              <FieldLabel>{copy.sns}</FieldLabel>
              <ChoiceGrid
                options={snsOptions}
                value={form.snsPlatform}
                disabled={!editing}
                onChange={(snsPlatform) => patchForm({ snsPlatform })}
              />
            </section>
          )}
        </div>
      )}

      {!loading && !unavailableReason && !showChooser && (
        <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-neutral-200 bg-white p-4">
          <div className="mx-auto flex max-w-3xl gap-3">
            <Button
              className="h-14 flex-1 text-lg"
              icon={<Printer className="h-5 w-5" />}
              style={{
                backgroundColor: '#fff',
                borderColor: '#171717',
                color: '#171717',
              }}
              onClick={openPrint}
            >
              {copy.print}
            </Button>
            {editing ? (
              <Button
                loading={submitting}
                className="h-14 flex-1 text-lg"
                style={{
                  backgroundColor: '#171717',
                  borderColor: '#171717',
                  color: '#fff',
                }}
                onClick={handleSubmit}
              >
                {copy.submit}
              </Button>
            ) : (
              <Button
                className="h-14 flex-1 text-lg"
                style={{
                  backgroundColor: '#fff',
                  borderColor: '#171717',
                  color: '#171717',
                }}
                onClick={() => setEditing(true)}
              >
                {copy.edit}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RecycleOrderReceptionForm;
